/**
 * Core Analytics Service
 * Manages multiple analytics providers with privacy-first approach
 */

import type { 
  AnalyticsProvider, 
  AnyAnalyticsEvent, 
  UserTraits, 
  AnalyticsConfig,
  ConsentPreferences 
} from './types';
import { GA4Provider } from './providers/ga4';
import { PlausibleProvider } from './providers/plausible';
import { 
  getConsentPreferences, 
  hasConsentFor, 
  onConsentChange 
} from './consent';
import { 
  hashIdentifier, 
  shouldTrackEvent,
  sanitizeUrl,
  sanitizeErrorMessage 
} from './privacy';

export class AnalyticsService {
  private providers: AnalyticsProvider[] = [];
  private config: AnalyticsConfig;
  private userId: string | null = null;
  private sessionId: string;
  private consentUnsubscribe: (() => void) | null = null;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    
    // Initialize providers based on config
    this.initializeProviders();
    
    // Listen for consent changes
    this.setupConsentListener();
    
    if (this.config.debug) {
      console.log('[Analytics] Service initialized', {
        providers: this.providers.map(p => p.name),
        sessionId: this.sessionId,
      });
    }
  }

  private initializeProviders(): void {
    // Only initialize if we have consent
    const consent = getConsentPreferences();
    if (!consent?.analytics) return;

    if (this.config.providers.ga4) {
      const ga4 = new GA4Provider(
        this.config.providers.ga4.measurementId,
        this.config.debug
      );
      if (ga4.enabled) {
        this.providers.push(ga4);
      }
    }

    if (this.config.providers.plausible) {
      const plausible = new PlausibleProvider(
        this.config.providers.plausible.domain,
        this.config.debug
      );
      if (plausible.enabled) {
        this.providers.push(plausible);
      }
    }
  }

  private setupConsentListener(): void {
    this.consentUnsubscribe = onConsentChange((preferences: ConsentPreferences) => {
      if (preferences.analytics && this.providers.length === 0) {
        // Consent granted, initialize providers
        this.initializeProviders();
      } else if (!preferences.analytics && this.providers.length > 0) {
        // Consent revoked, clear providers
        this.clearProviders();
      }
    });
  }

  private clearProviders(): void {
    this.providers.forEach(provider => provider.reset());
    this.providers = [];
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Track an analytics event
   */
  async track(event: Omit<AnyAnalyticsEvent, 'sessionId' | 'userId' | 'timestamp'>): Promise<void> {
    // Check consent
    const consent = getConsentPreferences();
    if (!consent || !shouldTrackEvent(event.category, consent)) {
      if (this.config.debug) {
        console.log('[Analytics] Event blocked by consent:', event);
      }
      return;
    }

    // Enrich event
    const enrichedEvent: AnyAnalyticsEvent = {
      ...event,
      sessionId: this.sessionId,
      userId: this.userId || undefined,
      timestamp: Date.now(),
    };

    // Send to all providers
    const promises = this.providers.map(provider => 
      provider.track(enrichedEvent).catch(error => {
        if (this.config.debug) {
          console.error(`[Analytics] ${provider.name} track error:`, error);
        }
      })
    );

    await Promise.all(promises);

    if (this.config.debug) {
      console.log('[Analytics] Event tracked:', enrichedEvent);
    }
  }

  /**
   * Identify a user
   */
  async identify(userId: string, traits?: UserTraits): Promise<void> {
    if (!hasConsentFor('analytics')) {
      if (this.config.debug) {
        console.log('[Analytics] Identify blocked by consent');
      }
      return;
    }

    // Hash the user ID for privacy
    const hashedUserId = hashIdentifier(userId);
    this.userId = hashedUserId;

    // Send to all providers
    const promises = this.providers.map(provider => 
      provider.identify(hashedUserId, traits).catch(error => {
        if (this.config.debug) {
          console.error(`[Analytics] ${provider.name} identify error:`, error);
        }
      })
    );

    await Promise.all(promises);

    if (this.config.debug) {
      console.log('[Analytics] User identified:', hashedUserId, traits);
    }
  }

  /**
   * Track a page view
   */
  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    if (!hasConsentFor('analytics')) {
      if (this.config.debug) {
        console.log('[Analytics] Page view blocked by consent');
      }
      return;
    }

    const pageName = name || document.title;
    const pageProps = {
      ...properties,
      url: sanitizeUrl(window.location.href),
      path: window.location.pathname,
      referrer: document.referrer,
    };

    // Send to all providers
    const promises = this.providers.map(provider => 
      provider.page(pageName, pageProps).catch(error => {
        if (this.config.debug) {
          console.error(`[Analytics] ${provider.name} page error:`, error);
        }
      })
    );

    await Promise.all(promises);

    if (this.config.debug) {
      console.log('[Analytics] Page tracked:', pageName, pageProps);
    }
  }

  /**
   * Track an error
   */
  async trackError(error: Error | string, context?: Record<string, any>): Promise<void> {
    // Errors are tracked even without full consent (essential)
    const consent = getConsentPreferences();
    if (!consent?.essential) return;

    const errorMessage = error instanceof Error ? error.message : error;
    const sanitizedMessage = sanitizeErrorMessage(errorMessage);

    await this.track({
      event: 'error_occurred',
      category: 'error',
      properties: {
        error_type: 'javascript',
        error_message: sanitizedMessage,
        page: window.location.pathname,
        ...context,
      },
    });
  }

  /**
   * Track performance metrics
   */
  async trackPerformance(metricName: string, value: number, unit: 'ms' | 'seconds' | 'bytes' | 'percent'): Promise<void> {
    // Performance is tracked as essential
    const consent = getConsentPreferences();
    if (!consent?.essential) return;

    await this.track({
      event: 'performance_metric',
      category: 'performance',
      properties: {
        metric_name: metricName,
        value,
        unit,
        page: window.location.pathname,
      },
    });
  }

  /**
   * Reset analytics (for logout)
   */
  async reset(): Promise<void> {
    this.userId = null;
    this.sessionId = this.generateSessionId();

    // Reset all providers
    const promises = this.providers.map(provider => 
      provider.reset().catch(error => {
        if (this.config.debug) {
          console.error(`[Analytics] ${provider.name} reset error:`, error);
        }
      })
    );

    await Promise.all(promises);

    if (this.config.debug) {
      console.log('[Analytics] Analytics reset');
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.consentUnsubscribe) {
      this.consentUnsubscribe();
    }
    this.clearProviders();
  }

  /**
   * Force reinitialize providers (useful after consent change)
   */
  reinitializeProviders(): void {
    // Clear existing providers
    this.clearProviders();
    
    // Reinitialize
    this.initializeProviders();
    
    if (this.config.debug) {
      console.log('[Analytics] Providers reinitialized:', {
        count: this.providers.length,
        providers: this.providers.map(p => p.name),
      });
    }
  }
}

// Singleton instance
let analyticsInstance: AnalyticsService | null = null;

/**
 * Initialize analytics service
 */
export function initializeAnalytics(config: AnalyticsConfig): AnalyticsService {
  if (analyticsInstance) {
    analyticsInstance.destroy();
  }
  
  analyticsInstance = new AnalyticsService(config);
  return analyticsInstance;
}

/**
 * Get analytics instance
 */
export function getAnalytics(): AnalyticsService | null {
  return analyticsInstance;
}